import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container, Box, Typography, TextField, Button,
    Select, MenuItem, FormControl, InputLabel, IconButton,
    CircularProgress, Slider, Popover, InputAdornment, Card, CardContent, Alert, Collapse,
    LinearProgress, Chip, Divider
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion, AnimatePresence } from 'framer-motion';

const ALL_PROVIDERS = [
    { id: 'Hargreaves Lansdown', label: 'Hargreaves Lansdown' },
    { id: 'Raisin', label: 'Raisin' },
    { id: 'Flagstone', label: 'Flagstone' },
];


const horizonOptions = [
    { value: 0, label: 'Easy access' },
    { value: 1, label: '1 month' },
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '1 year' },
    { value: 24, label: '2 years' },
    { value: 36, label: '3 years' },
    { value: 60, label: '5 years' }
];

// Function to calculate the next ISA limit refresh date (April 6th)
const getNextIsaRefreshDate = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11 (January is 0)
    const currentDay = now.getDate();
    
    // Create date for April 6th of current year (month 3 = April, day 6)
    const april6ThisYear = new Date(currentYear, 3, 6, 0, 0, 0);
    
    // If we're before April 6th this year, use this year's date
    // Otherwise, use next year's April 6th
    if (currentMonth < 3 || (currentMonth === 3 && currentDay < 6)) {
        // Before April 6th, use this year
        return april6ThisYear.toISOString();
    } else {
        // On or after April 6th, use next year
        const april6NextYear = new Date(currentYear + 1, 3, 6, 0, 0, 0);
        return april6NextYear.toISOString();
    }
};

// Custom hook for countdown timer
const useCountdown = (targetDate) => {
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
        const target = new Date(targetDate).getTime();
        
        const updateCountdown = () => {
            const now = new Date().getTime();
            const distance = target - now;

            if (distance > 0) {
                setCountdown({
                    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((distance % (1000 * 60)) / 1000)
                });
            } else {
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [targetDate]);

    return countdown;
};

// Countdown display component
const CountdownDisplay = ({ countdown }) => {
    const countdownItems = useMemo(() => [
        { value: countdown.days, label: 'Days', pad: false },
        { value: countdown.hours, label: 'Hours', pad: true },
        { value: countdown.minutes, label: 'Minutes', pad: true },
        { value: countdown.seconds, label: 'Seconds', pad: true }
    ], [countdown]);

    return (
        <Box 
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: '0 0 auto' }}
            role="timer"
            aria-live="polite"
            aria-label="Time remaining until ISA limit refresh"
        >
            {countdownItems.map((item, index) => (
                <React.Fragment key={item.label}>
                    {index > 0 && (
                        <Typography 
                            variant="body2" 
                            sx={{ fontWeight: 700, color: '#d32f2f', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                            aria-hidden="true"
                        >
                            :
                        </Typography>
                    )}
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 700, 
                                color: '#d32f2f', 
                                lineHeight: 1, 
                                fontSize: { xs: '0.7rem', sm: '0.875rem' }, 
                                mb: 0.25 
                            }}
                            aria-label={`${item.value} ${item.label.toLowerCase()}`}
                        >
                            {item.pad ? String(item.value).padStart(2, '0') : item.value}
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: '#d32f2f', 
                                textTransform: 'uppercase', 
                                fontSize: { xs: '0.4rem', sm: '0.55rem' }, 
                                letterSpacing: 0.5, 
                                display: 'block' 
                            }}
                            aria-hidden="true"
                        >
                            {item.label}
                        </Typography>
                    </Box>
                </React.Fragment>
            ))}
        </Box>
    );
};

const InputPage = () => {
    const [earnings, setEarnings] = useState('');
    const [displayEarnings, setDisplayEarnings] = useState('');
    const [totalSavings, setTotalSavings] = useState('');
    const [displayTotalSavings, setDisplayTotalSavings] = useState('');
    const [savingsGoals, setSavingsGoals] = useState([{ amount: '', displayAmount: '', horizon: 0 }]);
    const [isaAllowanceUsed, setIsaAllowanceUsed] = useState(0);
    const [otherSavingsIncome, setOtherSavingsIncome] = useState(0);
    const [excludedProviders, setExcludedProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSimpleView, setIsSimpleView] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showIsaSlider, setShowIsaSlider] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [savingsAnchorEl, setSavingsAnchorEl] = useState(null);
    const [showFineTuneSection, setShowFineTuneSection] = useState(false);
    const [formTouched, setFormTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [bannerExpanded, setBannerExpanded] = useState(false);
    const [surveyMode, setSurveyMode] = useState(false);
    const [surveyStep, setSurveyStep] = useState(0);
    const [surveyError, setSurveyError] = useState('');
    const [otherIncomeOption, setOtherIncomeOption] = useState(null); // null | 'none' | 'low' | 'high'
    const navigate = useNavigate();
    const location = useLocation();
    
    // Calculate next ISA refresh date and use custom countdown hook
    const nextIsaRefreshDate = useMemo(() => getNextIsaRefreshDate(), []);
    const countdown = useCountdown(nextIsaRefreshDate);
    
    // Memoize toggle handler
    const handleBannerToggle = useCallback(() => {
        setBannerExpanded(prev => !prev);
    }, []);

    const formatCurrency = (rawValue) => {
        if (!rawValue && rawValue !== 0) return '';
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(Number(rawValue));
    };

    // Form validation functions
    const validateEarnings = (value) => {
        const numValue = parseFloat(value);
        if (!value) return 'Annual earnings is required';
        if (isNaN(numValue)) return 'Please enter a valid number';
        if (numValue < 0) return 'Earnings cannot be negative';
        if (numValue > 10000000) return 'Please enter a realistic amount';
        return '';
    };

    const validateSavings = (value, fieldName = 'savings amount') => {
        const numValue = parseFloat(value);
        if (!value) return `${fieldName} is required`;
        if (isNaN(numValue)) return 'Please enter a valid number';
        if (numValue < 0) return 'Amount cannot be negative';
        if (numValue > 1000000) return 'Please enter a realistic amount';
        return '';
    };

    // Form persistence
    const saveFormToStorage = useCallback(() => {
        const formData = {
            earnings,
            totalSavings,
            savingsGoals,
            isaAllowanceUsed,
            excludedProviders,
            isSimpleView,
            showAdvanced,
            showIsaSlider,
            timestamp: Date.now()
        };
        localStorage.setItem('quantifyLiteForm', JSON.stringify(formData));
    }, [earnings, totalSavings, savingsGoals, isaAllowanceUsed, excludedProviders, isSimpleView, showAdvanced, showIsaSlider]);

    const loadFormFromStorage = () => {
        try {
            const saved = localStorage.getItem('quantifyLiteForm');
            if (saved) {
                const formData = JSON.parse(saved);
                // Only restore if saved within last 24 hours
                if (Date.now() - formData.timestamp < 24 * 60 * 60 * 1000) {
                    return formData;
                }
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
        return null;
    };

    const handleInfoClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'tax-info-popover' : undefined;

    const handleSavingsInfoClick = (event) => {
        setSavingsAnchorEl(event.currentTarget);
    };

    const handleSavingsInfoClose = () => {
        setSavingsAnchorEl(null);
    };

    const savingsInfoOpen = Boolean(savingsAnchorEl);
    const savingsInfoId = savingsInfoOpen ? 'savings-info-popover' : undefined;

    // Handle error messages from navigation state
    useEffect(() => {
        if (location.state?.error) {
            setErrorMessage(location.state.error);
            // Clear error from location state to prevent it from showing again on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        if (location.state?.inputs) {
            const { inputs, isSimpleAnalysis } = location.state;

            // Restore earnings
            const rawEarnings = inputs.earnings?.toString() || '';
            setEarnings(rawEarnings);
            setDisplayEarnings(formatCurrency(rawEarnings));

            // Restore ISA allowance and fine-tune options
            setIsaAllowanceUsed(inputs.isa_allowance_used || 0);
            setOtherSavingsIncome(inputs.other_savings_income || 0);
            setExcludedProviders(inputs.excluded_providers || []);
            setShowFineTuneSection(
                (inputs.isa_allowance_used > 0) ||
                (inputs.other_savings_income > 0) ||
                (inputs.excluded_providers && inputs.excluded_providers.length > 0)
            );

            // Restore view mode
            setIsSimpleView(isSimpleAnalysis);

            if (isSimpleAnalysis) {
                // Restore total savings
                const rawTotalSavings = inputs.savings_goals?.[0]?.amount?.toString() || '';
                setTotalSavings(rawTotalSavings);
                setDisplayTotalSavings(formatCurrency(rawTotalSavings));
                setSavingsGoals([{ amount: '', displayAmount: '', horizon: 0 }]);
            } else {
                // Restore savings goals breakdown
                const restoredGoals = (inputs.savings_goals || []).map(goal => {
                    const rawAmount = goal.amount?.toString() || '';
                    return {
                        amount: rawAmount,
                        displayAmount: formatCurrency(rawAmount),
                        horizon: goal.horizon
                    };
                });
                setSavingsGoals(restoredGoals.length > 0 ? restoredGoals : [{ amount: '', displayAmount: '', horizon: 0 }]);
                setTotalSavings('');
                setDisplayTotalSavings('');
            }
        } else {
            // Try to load saved form data if no state from navigation
            const savedData = loadFormFromStorage();
            if (savedData) {
                setEarnings(savedData.earnings || '');
                setDisplayEarnings(formatCurrency(savedData.earnings));
                setTotalSavings(savedData.totalSavings || '');
                setDisplayTotalSavings(formatCurrency(savedData.totalSavings));
                setSavingsGoals(savedData.savingsGoals || [{ amount: '', displayAmount: '', horizon: 0 }]);
                setIsaAllowanceUsed(savedData.isaAllowanceUsed || 0);
                setExcludedProviders(savedData.excludedProviders || []);
                setIsSimpleView(savedData.isSimpleView ?? true);
                setShowAdvanced(savedData.showAdvanced || false);
                setShowIsaSlider(savedData.showIsaSlider || false);
            }
        }
    }, [location.state]);

    // Auto-save form data when it changes
    useEffect(() => {
        if (formTouched) {
            const timeoutId = setTimeout(() => {
                saveFormToStorage();
            }, 1000); // Save 1 second after user stops typing
            return () => clearTimeout(timeoutId);
        }
    }, [earnings, totalSavings, savingsGoals, isaAllowanceUsed, excludedProviders, isSimpleView, showAdvanced, showIsaSlider, formTouched, saveFormToStorage]);


    const handleEarningsChange = (e) => {
        const rawValue = e.target.value.replace(/[^0-9]/g, '');
        setEarnings(rawValue);
        setFormTouched(true);
        if (surveyMode) setSurveyError('');

        if (rawValue) {
            const formattedValue = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(Number(rawValue));
            setDisplayEarnings(formattedValue);
        } else {
            setDisplayEarnings('');
        }
    };

    const handleTotalSavingsChange = (e) => {
        const rawValue = e.target.value.replace(/[^0-9]/g, '');
        setTotalSavings(rawValue);
        setFormTouched(true);
        if (surveyMode) setSurveyError('');

        if (rawValue) {
            const formattedValue = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(Number(rawValue));
            setDisplayTotalSavings(formattedValue);
        } else {
            setDisplayTotalSavings('');
        }
    };

    const handleAddGoal = () => {
        setSavingsGoals([...savingsGoals, { amount: '', displayAmount: '', horizon: 0 }]);
    };

    const handleGoalChange = (index, event) => {
        const { name, value } = event.target;
        setFormTouched(true);
        
        const newGoals = savingsGoals.map((goal, i) => {
            if (i === index) {
                if (name === 'amount') {
                    const rawValue = value.replace(/[^0-9]/g, '');
                    const formattedValue = rawValue
                        ? new Intl.NumberFormat('en-GB', {
                            style: 'currency',
                            currency: 'GBP',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        }).format(Number(rawValue))
                        : '';
                    return { ...goal, amount: rawValue, displayAmount: formattedValue };
                }
                return { ...goal, [name]: value };
            }
            return goal;
        });
        setSavingsGoals(newGoals);
    };

    const handleRemoveGoal = (index) => {
        const newGoals = savingsGoals.filter((_, i) => i !== index);
        setSavingsGoals(newGoals);
    };

    const handleFineTuneToggle = () => {
        setShowFineTuneSection((prev) => {
            const newShow = !prev;
            if (!newShow) {
                setIsaAllowanceUsed(0); // Full ISA left if hidden
                setOtherSavingsIncome(0); // Reset savings income if hidden
                setExcludedProviders([]); // Reset excluded providers if hidden
            }
            return newShow;
        });
    };

    const handleProviderToggle = (providerId) => {
        setExcludedProviders(prev =>
            prev.includes(providerId)
                ? prev.filter(p => p !== providerId)
                : [...prev, providerId]
        );
    };

    // Survey mode definitions
    const surveySteps = [
        {
            title: 'What are your annual earnings?',
            description: 'This is your total income before tax for the current tax year (6th April to 5th April). Include your salary, bonuses, dividends, rental income, and any other sources of income. Don\'t worry about being exact — a rough estimate is perfectly fine!',
            field: 'earnings',
        },
        {
            title: 'How much would you like to save?',
            description: 'Enter the total amount of money you\'d like to put into savings right now. This should be money you currently have available and can access — don\'t include anything locked away in pensions or fixed-term accounts that haven\'t matured.',
            field: 'savings',
        },
        {
            title: 'Have you used any of your ISA allowance?',
            description: 'Every tax year you can save up to £20,000 into ISAs (Individual Savings Accounts) tax-free. If you\'ve already deposited money into an ISA this tax year, let us know how much so we can work with what\'s left. If you haven\'t, or you\'re not sure, just leave this at £0.',
            field: 'isa',
        },
        {
            title: 'Do you earn interest from other savings?',
            description: 'If you have money in other savings accounts that are already earning interest (e.g. a fixed-term bond or another easy-access account), enter the approximate annual interest you receive. This helps us calculate your remaining tax-free Personal Savings Allowance. If you\'re not sure, just leave this at £0.',
            field: 'otherIncome',
        },
    ];

    const handleSurveyNext = () => {
        let error = '';
        if (surveyStep === 0) {
            error = validateEarnings(earnings);
        } else if (surveyStep === 1) {
            error = validateSavings(totalSavings, 'total savings amount');
        }
        // Step 2 (ISA slider) always has a valid value
        // Step 3 requires an option to be chosen
        if (surveyStep === 3 && !otherIncomeOption) {
            setSurveyError('Please select an option to continue.');
            return;
        }

        if (error) {
            setSurveyError(error);
            return;
        }

        setSurveyError('');

        if (surveyStep < surveySteps.length - 1) {
            setSurveyStep(prev => prev + 1);
        } else {
            // Final step — submit with simple view and current values
            setIsSimpleView(true);
            setLoading(true);
            const inputs = {
                earnings: parseFloat(earnings),
                savings_goals: [{
                    amount: parseFloat(totalSavings),
                    horizon: 0,
                }],
                isa_allowance_used: isaAllowanceUsed,
                other_savings_income: otherSavingsIncome,
                excluded_providers: excludedProviders,
            };
            navigate('/loading', {
                state: {
                    inputs: inputs,
                    isSimpleAnalysis: true
                }
            });
        }
    };

    const handleSurveyBack = () => {
        setSurveyError('');
        if (surveyStep === 0) {
            setSurveyMode(false);
        } else {
            // If navigating back from step 3 (otherIncome), reset the option so it shows fresh buttons
            if (surveyStep === 3) {
                setOtherIncomeOption(null);
                setOtherSavingsIncome(0);
            }
            setSurveyStep(prev => prev - 1);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submission
        const earningsValidation = validateEarnings(earnings);
        const savingsValidation = isSimpleView 
            ? validateSavings(totalSavings, 'total savings amount')
            : '';
        
        // Check savings goals validation in breakdown mode
        let hasGoalErrors = false;
        if (!isSimpleView) {
            const goalErrors = savingsGoals.some(goal => {
                const error = validateSavings(goal.amount, 'savings goal amount');
                return error !== '';
            });
            hasGoalErrors = goalErrors;
        }
        
        if (earningsValidation || savingsValidation || hasGoalErrors) {
            return;
        }
        
        setLoading(true);

        // Prepare the data to pass to the loading page
        const inputs = isSimpleView 
            ? {
                earnings: parseFloat(earnings),
                savings_goals: [{
                    amount: parseFloat(totalSavings),
                    horizon: 0, // This will be overridden in loading page
                }],
                isa_allowance_used: isaAllowanceUsed,
                other_savings_income: otherSavingsIncome,
                excluded_providers: excludedProviders,
            }
            : {
                earnings: parseFloat(earnings),
                savings_goals: savingsGoals.map(goal => ({
                    amount: parseFloat(goal.amount),
                    horizon: goal.horizon,
                })),
                isa_allowance_used: isaAllowanceUsed,
                other_savings_income: otherSavingsIncome,
                excluded_providers: excludedProviders,
            };

        // Navigate to loading page with the inputs
        navigate('/loading', {
            state: {
                inputs: inputs,
                isSimpleAnalysis: isSimpleView
            }
        });
    };

    return (
        <Container maxWidth={surveyMode ? false : 'md'} disableGutters={surveyMode} sx={{ py: surveyMode ? 0 : { xs: 3, sm: 6 } }}>
            {!surveyMode && (
            <>
            {/* Countdown Banner */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Box
                    onClick={handleBannerToggle}
                    sx={{ cursor: 'pointer', mb: 4 }}
                    aria-label={bannerExpanded ? 'Hide ISA limit refresh details' : 'Show ISA limit refresh details'}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleBannerToggle();
                        }
                    }}
                >
                    <Alert 
                        severity="error"
                        icon={<WarningAmberIcon sx={{ fontSize: '1rem' }} />}
                        sx={{ 
                            borderRadius: 3,
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            border: '2px solid #d32f2f',
                            color: '#2D1B4E',
                            alignItems: 'flex-start',
                            py: 1,
                            '& .MuiAlert-icon': {
                                color: '#d32f2f',
                                alignSelf: 'flex-start',
                                marginTop: 0,
                            },
                            '& .MuiAlert-message': {
                                width: '100%',
                                paddingTop: 0,
                                paddingBottom: 0,
                                paddingRight: 0,
                                flex: 1,
                            },
                        }}
                    >
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                flexDirection: 'row',
                                flexWrap: 'nowrap', 
                                gap: 1,
                                width: '100%',
                                minWidth: 0,
                                minHeight: '24px'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D1B4E', lineHeight: 1.4, fontSize: { xs: '0.7rem', sm: '0.875rem' }, textAlign: 'left', flex: '0 0 auto', minWidth: 0 }}>
                                    ISA Refresh in
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>
                                    <Box sx={{ flex: '0 0 auto' }}>
                                        <CountdownDisplay countdown={countdown} />
                                    </Box>
                                    {bannerExpanded ? <ExpandLessIcon fontSize="small" sx={{ color: '#d32f2f' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: '#d32f2f' }} />}
                                </Box>
                            </Box>
                            
                            <Collapse in={bannerExpanded}>
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(211, 47, 47, 0.2)' }}>
                                    <Typography variant="body2" sx={{ mb: 2, color: '#6B5B8A', lineHeight: 1.6 }}>
                                        Your ISA allowance resets annually on <strong>April 6th</strong>, the start of the new tax year. Any unused allowance from the current tax year cannot be carried forward.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, color: '#d32f2f', fontWeight: 600, lineHeight: 1.6 }}>
                                        Use this tool to optimize your savings and make the most of your current tax-free ISA allowance before it expires.
                                    </Typography>
                                </Box>
                            </Collapse>
                        </Box>
                    </Alert>
                </Box>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Collapse in={!!errorMessage}>
                        <Alert 
                            severity="error" 
                            onClose={() => setErrorMessage(null)}
                            sx={{ mb: 3, textAlign: 'left' }}
                        >
                            <Typography variant="body2" component="div">
                                <strong>Optimization failed:</strong> {errorMessage}
                            </Typography>
                        </Alert>
                    </Collapse>
                    <Typography 
                        variant="h3" 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 700,
                            color: '#2D1B4E',
                            mb: 2,
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Just Save It.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                        No time to sort out your savings? Overwhelmed by your options? In a tangle over your tax? Intimidated by ISAs?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Answer these two questions and get your savings sorted.
                    </Typography>
                </Box>

                {/* Survey CTA */}
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <Button
                        type="button"
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={() => { setSurveyMode(true); setSurveyStep(0); setSurveyError(''); }}
                        sx={{
                            py: 2,
                            fontSize: '1rem',
                            fontWeight: 600,
                            backgroundColor: '#2D1B4E',
                            color: '#FFFFFF',
                            borderRadius: 3,
                            textTransform: 'none',
                            lineHeight: 1.4,
                            '&:hover': {
                                backgroundColor: '#3D2B5E',
                                boxShadow: '0 6px 24px rgba(45, 27, 78, 0.3)',
                            },
                        }}
                    >
                        Not sure where to start? Take the survey!
                    </Button>
                </motion.div>

                {/* Or divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                    <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(155, 126, 222, 0.3)' }} />
                    <Typography variant="body2" sx={{ px: 2, color: '#6B5B8A', fontWeight: 500 }}>
                        or
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(155, 126, 222, 0.3)' }} />
                </Box>

                <form onSubmit={handleSubmit}>
                    <Card sx={{ mb: 3, borderRadius: 3 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                            <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleInfoClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            sx: {
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(155, 126, 222, 0.2)',
                                border: '1px solid rgba(155, 126, 222, 0.2)',
                            }
                        }}
                            >
                                <Box sx={{ p: 3, maxWidth: 400 }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#2D1B4E', fontWeight: 600 }}>Don't Worry!</Typography>
                                    <Typography variant="body2" paragraph sx={{ color: '#6B5B8A' }}>
                                        A rough estimate is all we need! You only have to be accurate if you are near:
                                    </Typography>
                                    <Typography variant="body2" paragraph sx={{ color: '#6B5B8A' }}>
                                        <strong>£50,270 (when tax goes from 20% to 40%)</strong>
                                    </Typography>
                                    <Typography variant="body2" paragraph sx={{ color: '#6B5B8A' }}>
                                        <strong>£125,140 (when tax goes from 40% to 45%)</strong>
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#6B5B8A' }}>
                                        By earnings we mean your total income; salary, bonuses, dividends, rent receipts, etc. before tax.
                                    </Typography>
                                </Box>
                            </Popover>
                            <Popover
                        id={savingsInfoId}
                        open={savingsInfoOpen}
                        anchorEl={savingsAnchorEl}
                        onClose={handleSavingsInfoClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            sx: {
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(155, 126, 222, 0.2)',
                                border: '1px solid rgba(155, 126, 222, 0.2)',
                            }
                        }}
                            >
                                <Box sx={{ p: 3, maxWidth: 400 }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#2D1B4E', fontWeight: 600 }}>About Total Savings</Typography>
                                    <Typography variant="body2" sx={{ color: '#6B5B8A' }}>
                                        Please enter the amount you're looking to save. Don't include the money you can't access because it's locked away.
                                    </Typography>
                                </Box>
                            </Popover>
                            
                            <Typography variant="h6" sx={{ mb: 3, color: '#2D1B4E', fontWeight: 600 }}>
                                Roughly how much...
                            </Typography>
                    
                    <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                    >
                        <TextField
                            fullWidth
                            label="...will you earn this tax year?"
                            value={displayEarnings}
                            onChange={handleEarningsChange}
                            placeholder="e.g., £50,000"
                            required
                            variant="outlined"
                            margin="normal"
                            inputProps={{ inputMode: 'numeric' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            onClick={handleInfoClick} 
                                            edge="end"
                                            sx={{ color: '#9B7EDE' }}
                                        >
                                            <InfoIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                            />
                        </motion.div>

                        {isSimpleView ? (
                        <>
                            <motion.div
                                whileHover={{ scale: 1.01 }}
                                transition={{ duration: 0.2 }}
                            >
                                <TextField
                                    fullWidth
                                    label="...would you like to save?"
                                    value={displayTotalSavings}
                                    onChange={handleTotalSavingsChange}
                                    placeholder="e.g., £25,000"
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    inputProps={{ inputMode: 'numeric' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton 
                                                    onClick={handleSavingsInfoClick} 
                                                    edge="end"
                                                    sx={{ color: '#9B7EDE' }}
                                                >
                                                    <InfoIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 3 }}
                                    />
                                </motion.div>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={() => setIsSimpleView(false)}
                                    sx={{ flex: 1, minWidth: '150px' }}
                                >
                                    Savings Breakdown
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleFineTuneToggle}
                                    sx={{ flex: 1, minWidth: '150px' }}
                                >
                                    {showFineTuneSection ? 'Hide Fine-tune' : 'Fine-tune Options'}
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" component="h2" sx={{ mb: 3, color: '#2D1B4E', fontWeight: 600 }}>
                                    Savings Breakdown
                                </Typography>
                            {savingsGoals.map((goal, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                >
                                    <Card 
                                        variant="outlined" 
                                        sx={{ 
                                            p: 2.5, 
                                            mb: 2, 
                                            position: 'relative', 
                                            borderRadius: 3,
                                            border: '1px solid rgba(155, 126, 222, 0.2)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                                            <TextField
                                                label="Amount to Save"
                                                name="amount"
                                                value={goal.displayAmount}
                                                onChange={(e) => handleGoalChange(index, e)}
                                                placeholder="e.g., £10,000"
                                                required
                                                sx={{ flexGrow: 1 }}
                                                inputProps={{ inputMode: 'numeric' }}
                                            />
                                            <FormControl sx={{ minWidth: {sm: 180}, width: { xs: '100%', sm: 'auto' } }}>
                                                <InputLabel>Time Horizon</InputLabel>
                                                <Select
                                                    name="horizon"
                                                    value={goal.horizon}
                                                    onChange={(e) => handleGoalChange(index, e)}
                                                    label="Time Horizon"
                                                >
                                                    {horizonOptions.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {savingsGoals.length > 1 && (
                                                <IconButton 
                                                    onClick={() => handleRemoveGoal(index)} 
                                                    sx={{ 
                                                        color: '#9B7EDE',
                                                        position: { xs: 'absolute', sm: 'static' }, 
                                                        top: 16, 
                                                        right: 8
                                                    }}
                                                >
                                                    <RemoveCircleOutlineIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Card>
                                </motion.div>
                            ))}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                <Button
                                    type="button"
                                    onClick={handleAddGoal}
                                    startIcon={<AddCircleOutlineIcon />}
                                    variant="outlined"
                                >
                                    Add Another Savings Goal
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={() => setIsSimpleView(true)}
                                    sx={{ flex: 1, minWidth: '150px' }}
                                >
                                    Use Total Savings
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleFineTuneToggle}
                                    sx={{ flex: 1, minWidth: '150px' }}
                                >
                                    {showFineTuneSection ? 'Hide Fine-tune' : 'Fine-tune Options'}
                                    </Button>
                                </Box>
                            </>
                        )}

                        {showFineTuneSection && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Card sx={{ mt: 3, borderRadius: 3, border: '1px solid rgba(155, 126, 222, 0.2)' }}>
                                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2D1B4E' }}>
                                        Fine-tune Your Optimization
                                    </Typography>
                                    
                                    <Box sx={{ mb: 4 }}>
                                        <Typography id="isa-slider-label" gutterBottom sx={{ fontWeight: 500, color: '#2D1B4E', mb: 2 }}>
                                            ISA Allowance Used (£{isaAllowanceUsed.toLocaleString()})
                                        </Typography>
                                        <Box sx={{ px: 1 }}>
                                            <Slider
                                                aria-labelledby="isa-slider-label"
                                                value={isaAllowanceUsed}
                                                onChange={(e, newValue) => setIsaAllowanceUsed(newValue)}
                                                valueLabelDisplay="auto"
                                                step={500}
                                                marks
                                                min={0}
                                                max={20000}
                                                sx={{
                                                    color: '#9B7EDE',
                                                    '& .MuiSlider-thumb': {
                                                        '&:hover': {
                                                            boxShadow: '0 0 0 8px rgba(155, 126, 222, 0.16)',
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography id="savings-income-slider-label" gutterBottom sx={{ fontWeight: 500, color: '#2D1B4E', mb: 2 }}>
                                            Other Savings Income (£{otherSavingsIncome.toLocaleString()}{otherSavingsIncome >= 1000 ? ' or more' : ''})
                                        </Typography>
                                        <Box sx={{ px: 1 }}>
                                            <Slider
                                                aria-labelledby="savings-income-slider-label"
                                                value={otherSavingsIncome}
                                                onChange={(e, newValue) => setOtherSavingsIncome(newValue)}
                                                valueLabelDisplay="auto"
                                                step={50}
                                                marks
                                                min={0}
                                                max={1000}
                                                sx={{
                                                    color: '#9B7EDE',
                                                    '& .MuiSlider-thumb': {
                                                        '&:hover': {
                                                            boxShadow: '0 0 0 8px rgba(155, 126, 222, 0.16)',
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                            Enter any savings interest you're already earning from other accounts
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ my: 3, borderColor: 'rgba(155, 126, 222, 0.15)' }} />

                                    <Box>
                                        <Typography gutterBottom sx={{ fontWeight: 500, color: '#2D1B4E', mb: 0.5 }}>
                                            Exclude Providers
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Toggle off any providers you don't want included in the optimisation
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {ALL_PROVIDERS.map(provider => {
                                                const isExcluded = excludedProviders.includes(provider.id);
                                                return (
                                                    <Chip
                                                        key={provider.id}
                                                        label={provider.label}
                                                        onClick={() => handleProviderToggle(provider.id)}
                                                        icon={isExcluded ? <BlockIcon sx={{ fontSize: '1rem !important' }} /> : <CheckCircleOutlineIcon sx={{ fontSize: '1rem !important' }} />}
                                                        variant={isExcluded ? 'outlined' : 'filled'}
                                                        sx={{
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            ...(isExcluded ? {
                                                                color: '#9e9e9e',
                                                                borderColor: '#e0e0e0',
                                                                backgroundColor: 'transparent',
                                                                '& .MuiChip-icon': { color: '#9e9e9e' },
                                                                textDecoration: 'line-through',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(155, 126, 222, 0.06)',
                                                                    borderColor: '#9B7EDE',
                                                                },
                                                            } : {
                                                                backgroundColor: 'rgba(155, 126, 222, 0.12)',
                                                                color: '#2D1B4E',
                                                                border: '1px solid rgba(155, 126, 222, 0.3)',
                                                                '& .MuiChip-icon': { color: '#9B7EDE' },
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(155, 126, 222, 0.2)',
                                                                },
                                                            }),
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                        {excludedProviders.length > 0 && (
                                            <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: '#9e9e9e', fontStyle: 'italic' }}>
                                                {excludedProviders.length === ALL_PROVIDERS.length
                                                    ? 'All providers excluded — no results will be found.'
                                                    : `Excluding: ${excludedProviders.join(', ')}`}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </motion.div>
                        )}
                        </CardContent>
                    </Card>

                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={loading}
                            sx={{
                                mt: 2,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: '#FFFFFF' }} />
                            ) : (
                                'Optimise Savings'
                            )}
                        </Button>
                    </motion.div>
                </form>
            </motion.div>
            </>
            )}

            {surveyMode && (
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        px: { xs: 3, sm: 4 },
                        py: { xs: 4, sm: 6 },
                        position: 'relative',
                    }}
                >
                    {/* Back to form link — fixed top-left */}
                    <Box sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 } }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => { setSurveyMode(false); setSurveyStep(0); setSurveyError(''); }}
                            sx={{ color: '#6B5B8A', textTransform: 'none', fontWeight: 500 }}
                        >
                            Back to full form
                        </Button>
                    </Box>

                    {/* Progress bar */}
                    <Box sx={{ width: '100%', maxWidth: 600, mb: 5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={((surveyStep + 1) / surveySteps.length) * 100}
                            sx={{
                                borderRadius: 2,
                                height: 6,
                                backgroundColor: 'rgba(155, 126, 222, 0.15)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#9B7EDE',
                                    borderRadius: 2,
                                    transition: 'transform 0.4s ease',
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: '#6B5B8A' }}>
                            Step {surveyStep + 1} of {surveySteps.length}
                        </Typography>
                    </Box>

                    {/* Step content */}
                    <Box sx={{ width: '100%', maxWidth: 600 }}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={surveyStep}
                                initial={{ opacity: 0, x: 60 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -60 }}
                                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                            >
                                <Box sx={{ textAlign: 'center', mb: 5 }}>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#2D1B4E',
                                            mb: 2,
                                            fontSize: { xs: '1.75rem', sm: '2.25rem' },
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        {surveySteps[surveyStep].title}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: '#6B5B8A', lineHeight: 1.8, maxWidth: 520, mx: 'auto' }}
                                    >
                                        {surveySteps[surveyStep].description}
                                    </Typography>
                                </Box>

                                <Card sx={{ borderRadius: 3, mb: 4 }}>
                                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                        {/* Earnings input */}
                                        {surveyStep === 0 && (
                                            <TextField
                                                fullWidth
                                                label="Annual earnings"
                                                value={displayEarnings}
                                                onChange={handleEarningsChange}
                                                placeholder="e.g., £50,000"
                                                variant="outlined"
                                                inputProps={{ inputMode: 'numeric' }}
                                                error={!!surveyError}
                                                helperText={surveyError}
                                                autoFocus
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSurveyNext(); } }}
                                            />
                                        )}

                                        {/* Savings input */}
                                        {surveyStep === 1 && (
                                            <TextField
                                                fullWidth
                                                label="Amount to save"
                                                value={displayTotalSavings}
                                                onChange={handleTotalSavingsChange}
                                                placeholder="e.g., £25,000"
                                                variant="outlined"
                                                inputProps={{ inputMode: 'numeric' }}
                                                error={!!surveyError}
                                                helperText={surveyError}
                                                autoFocus
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSurveyNext(); } }}
                                            />
                                        )}

                                        {/* ISA Allowance slider */}
                                        {surveyStep === 2 && (
                                            <Box>
                                                <Typography gutterBottom sx={{ fontWeight: 500, color: '#2D1B4E', mb: 1, textAlign: 'center' }}>
                                                    ISA Allowance Used
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#9B7EDE', textAlign: 'center', mb: 3 }}>
                                                    £{isaAllowanceUsed.toLocaleString()}
                                                </Typography>
                                                <Box sx={{ px: { xs: 1, sm: 3 } }}>
                                                    <Slider
                                                        value={isaAllowanceUsed}
                                                        onChange={(e, newValue) => setIsaAllowanceUsed(newValue)}
                                                        valueLabelDisplay="auto"
                                                        valueLabelFormat={(v) => `£${v.toLocaleString()}`}
                                                        step={500}
                                                        marks={[
                                                            { value: 0, label: '£0' },
                                                            { value: 10000, label: '£10k' },
                                                            { value: 20000, label: '£20k' },
                                                        ]}
                                                        min={0}
                                                        max={20000}
                                                        sx={{
                                                            color: '#9B7EDE',
                                                            height: 8,
                                                            '& .MuiSlider-thumb': {
                                                                width: 24,
                                                                height: 24,
                                                                '&:hover': {
                                                                    boxShadow: '0 0 0 8px rgba(155, 126, 222, 0.16)',
                                                                },
                                                            },
                                                            '& .MuiSlider-markLabel': {
                                                                color: '#6B5B8A',
                                                                fontSize: '0.75rem',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Other Savings Income — option buttons + conditional slider */}
                                        {surveyStep === 3 && (
                                            <Box>
                                                {/* Three option buttons */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {[
                                                        { key: 'none', label: 'None', sub: 'I don\'t earn interest from other savings' },
                                                        { key: 'low',  label: '£0 – £1,000', sub: 'I earn some interest — let me be more specific' },
                                                        { key: 'high', label: 'Over £1,000', sub: 'I earn more than £1,000 interest per year' },
                                                    ].map(opt => {
                                                        const selected = otherIncomeOption === opt.key;
                                                        return (
                                                            <Box
                                                                key={opt.key}
                                                                onClick={() => {
                                                                    setOtherIncomeOption(opt.key);
                                                                    setSurveyError('');
                                                                    if (opt.key === 'none') {
                                                                        setOtherSavingsIncome(0);
                                                                    } else if (opt.key === 'high') {
                                                                        setOtherSavingsIncome(1000);
                                                                    } else {
                                                                        // 'low' — keep current slider value or reset to a sensible default
                                                                        setOtherSavingsIncome(prev => prev > 0 && prev < 950 ? prev : 100);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    p: 2,
                                                                    borderRadius: 2,
                                                                    border: selected
                                                                        ? '2px solid #9B7EDE'
                                                                        : '2px solid rgba(155, 126, 222, 0.25)',
                                                                    backgroundColor: selected
                                                                        ? 'rgba(155, 126, 222, 0.1)'
                                                                        : 'transparent',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        borderColor: '#9B7EDE',
                                                                        backgroundColor: 'rgba(155, 126, 222, 0.06)',
                                                                    },
                                                                }}
                                                            >
                                                                <Typography sx={{ fontWeight: 600, color: selected ? '#9B7EDE' : '#2D1B4E', fontSize: '1rem' }}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: '#6B5B8A', mt: 0.25 }}>
                                                                    {opt.sub}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>

                                                {/* Validation error */}
                                                {surveyError && (
                                                    <Typography variant="body2" sx={{ mt: 2, color: 'error.main', textAlign: 'center' }}>
                                                        {surveyError}
                                                    </Typography>
                                                )}

                                                {/* Slider — only shown when '£0–£1,000' is selected */}
                                                {otherIncomeOption === 'low' && (
                                                    <Box sx={{ mt: 4 }}>
                                                        <Typography gutterBottom sx={{ fontWeight: 500, color: '#2D1B4E', mb: 1, textAlign: 'center' }}>
                                                            Roughly how much?
                                                        </Typography>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#9B7EDE', textAlign: 'center', mb: 3 }}>
                                                            £{otherSavingsIncome.toLocaleString()}
                                                        </Typography>
                                                        <Box sx={{ px: { xs: 1, sm: 3 } }}>
                                                            <Slider
                                                                value={otherSavingsIncome}
                                                                onChange={(e, newValue) => setOtherSavingsIncome(newValue)}
                                                                valueLabelDisplay="auto"
                                                                valueLabelFormat={(v) => `£${v.toLocaleString()}`}
                                                                step={50}
                                                                marks={[
                                                                    { value: 0, label: '£0' },
                                                                    { value: 500, label: '£500' },
                                                                    { value: 950, label: '£950' },
                                                                ]}
                                                                min={0}
                                                                max={950}
                                                                sx={{
                                                                    color: '#9B7EDE',
                                                                    height: 8,
                                                                    '& .MuiSlider-thumb': {
                                                                        width: 24,
                                                                        height: 24,
                                                                        '&:hover': {
                                                                            boxShadow: '0 0 0 8px rgba(155, 126, 222, 0.16)',
                                                                        },
                                                                    },
                                                                    '& .MuiSlider-markLabel': {
                                                                        color: '#6B5B8A',
                                                                        fontSize: '0.75rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={handleSurveyBack}
                                    sx={{ px: { xs: 3, sm: 4 }, py: 1.5 }}
                                >
                                    {surveyStep === 0 ? 'Exit' : 'Back'}
                                </Button>
                            </motion.div>

                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={surveyStep < surveySteps.length - 1 ? <ArrowForwardIcon /> : null}
                                    onClick={handleSurveyNext}
                                    disabled={loading}
                                    sx={{
                                        px: { xs: 3, sm: 4 },
                                        py: 1.5,
                                        fontWeight: 600,
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} sx={{ color: '#FFFFFF' }} />
                                    ) : surveyStep < surveySteps.length - 1 ? (
                                        'Continue'
                                    ) : (
                                        'Optimise Savings'
                                    )}
                                </Button>
                            </motion.div>
                        </Box>
                    </Box>
                </Box>
            )}
        </Container>
    );
};

export default InputPage; 